// src/services/transactionFetcher/index.js

import { supportedChains } from '../../chains';
import { fetchTransactions as fetchBitrockTransactions } from './bitrockTransactionFetcher';
import { fetchTransactions as fetchEthereumTransactions } from './ethereumTransactionFetcher';
import { fetchTransactions as fetchBaseTransactions } from './baseTransactionFetcher';
import { fetchTransactions as fetchArbitrumTransactions } from './arbitrumTransactionFetcher';
import { fetchTransactions as fetchAvalancheTransactions } from './avalancheTransactionFetcher';
import { fetchTransactions as fetchPolygonTransactions } from './polygonTransactionFetcher';
import { fetchTransactions as fetchCeloTransactions } from './celoTransactionFetcher';
import { fetchTransactions as fetchOptimismTransactions } from './optimismTransactionFetcher';
import { fetchTransactions as fetchCronosTransactions } from './cronosTransactionFetcher';
import { fetchTransactions as fetchOmaxTransactions } from './omaxTransactionFetcher';
import { fetchTransactions as fetchBscTransactions } from './bscTransactionFetcher';
import { fetchTransactions as fetchTaraxaTransactions } from './taraxaTransactionFetcher';
import { fetchTransactions as fetchInfinaeonTransactions } from './infinaeonTransactionFetcher';

import { mergeTransactions, standardizeTransactionFormat } from './transactionFormatUtils';

// Map of chain keys to their specific transaction fetcher implementations
const TRANSACTION_FETCHERS = {
  'bitrock': fetchBitrockTransactions,
  'ethereum': fetchEthereumTransactions,
  'base': fetchBaseTransactions,
  'arbitrum': fetchArbitrumTransactions,
  'avalanche': fetchAvalancheTransactions,
  'polygon': fetchPolygonTransactions,
  'celo': fetchCeloTransactions,
  'optimism': fetchOptimismTransactions,
  'cronos': fetchCronosTransactions,
  'omax': fetchOmaxTransactions,
  'bsc': fetchBscTransactions,
  'taraxa': fetchTaraxaTransactions,
  'infinaeon': fetchInfinaeonTransactions,

  // Add new chains and their fetchers here
};

/**
 * Generic transaction fetcher implementation suitable for chains
 * that follow the standard block explorer API format
 */
const fetchGenericTransactions = async (chainKey, walletAddress, options = {}) => {
  try {
    const chainConfig = supportedChains[chainKey];
    if (!chainConfig) {
      throw new Error(`Unsupported chain: ${chainKey}`);
    }
    
    const { transactionApiEndpoint, transactionResponseMapper } = chainConfig;
    
    if (!transactionApiEndpoint || !transactionResponseMapper) {
      throw new Error(`Chain ${chainKey} does not have required transaction API configuration`);
    }
    
    // Replace {address} placeholder in API endpoint
    const url = transactionApiEndpoint.replace('{address}', walletAddress);
    
    // Add query parameters if available
    let finalUrl = url;
    if (options.limit) {
      finalUrl += finalUrl.includes('?') ? `&limit=${options.limit}` : `?limit=${options.limit}`;
    }
    
    if (options.nextToken) {
      finalUrl += `&next=${options.nextToken}`;
    }
    
    const response = await fetch(finalUrl);
    const data = await response.json();
    
    // Use the provided mapper function to transform the API response
    return transactionResponseMapper(data, walletAddress, options);
  } catch (error) {
    console.error(`Error fetching ${chainKey} transactions:`, error);
    return { transactions: [], hasMore: false, nextToken: null };
  }
};

/**
 * Fetch transactions for a given wallet address based on the chain.
 * This function handles chain-specific transaction fetching logic
 * and will work with any chain that has the appropriate configuration.
 *
 * @param {string} chainKey - The key of the chain (e.g., 'bitrock' or 'ethereum')
 * @param {string} walletAddress - The wallet address for which to fetch transactions
 * @param {Object} options - Additional options like limit, page, nextToken
 * @returns {Promise<{transactions: Array, hasMore: boolean, nextToken: string}>} - Transactions and pagination info
 */
export const fetchTransactions = async (chainKey, walletAddress, options = {}) => {
  // First check if we have a custom fetcher implementation for this chain
  if (TRANSACTION_FETCHERS[chainKey]) {
    try {
      const chainConfig = supportedChains[chainKey];
      if (!chainConfig) {
        console.error(`Chain config not found for: ${chainKey}`);
        return { transactions: [], hasMore: false, nextToken: null };
      }
      
      const result = await TRANSACTION_FETCHERS[chainKey](walletAddress, options, chainConfig);
      return result;
    } catch (error) {
      console.error(`Error in custom fetcher for ${chainKey}:`, error);
      return { transactions: [], hasMore: false, nextToken: null };
    }
  }
  
  // For chains without a custom fetcher, check if they have a transactionApiEndpoint
  const chainConfig = supportedChains[chainKey];
  if (!chainConfig) {
    console.warn(`Unsupported chain: ${chainKey}`);
    return { transactions: [], hasMore: false, nextToken: null };
  }
  
  // Check if this chain has a transactionApiEndpoint and responseMapper
  if (chainConfig.transactionApiEndpoint && chainConfig.transactionResponseMapper) {
    try {
      const result = await fetchGenericTransactions(chainKey, walletAddress, options);
      return result;
    } catch (error) {
      console.error(`Error in generic fetcher for ${chainKey}:`, error);
      return { transactions: [], hasMore: false, nextToken: null };
    }
  }
  
  // If no custom fetcher or generic API endpoint is available
  console.warn(`No transaction fetcher defined for chain: ${chainKey}`);
  return { transactions: [], hasMore: false, nextToken: null };
};

/**
 * Register a new transaction fetcher implementation for a specific chain
 * 
 * @param {string} chainKey - The key of the chain
 * @param {function} fetcherFunction - The transaction fetcher implementation
 */
export const registerTransactionFetcher = (chainKey, fetcherFunction) => {
  if (typeof fetcherFunction !== 'function') {
    throw new Error('fetcherFunction must be a function');
  }
  
  TRANSACTION_FETCHERS[chainKey] = fetcherFunction;
};

/**
 * Fetch transactions from API and combine with local storage data
 * @param {string} chainKey - Chain key
 * @param {string} walletAddress - Wallet address
 * @param {Array} localTransactions - Transactions from localStorage
 * @param {Object} options - Additional options
 * @returns {Promise<{transactions: Array, hasMore: boolean, nextToken: string}>}
 */
export const fetchAndMergeTransactions = async (chainKey, walletAddress, localTransactions, options = {}) => {
  try {
    // Fetch transactions from API
    const { transactions: apiTransactions, hasMore, nextToken } = 
      await fetchTransactions(chainKey, walletAddress, options);
    
    // Filter local transactions for this chain and address
    const lowerWalletAddress = walletAddress.toLowerCase();
    const relevantLocalTxs = Array.isArray(localTransactions) ? localTransactions.filter(tx => {
      const isRelevantChain = tx.network === chainKey;
      const fromMatches = tx.from && tx.from.toLowerCase() === lowerWalletAddress;
      const toMatches = tx.to && tx.to.toLowerCase() === lowerWalletAddress;
      return isRelevantChain && (fromMatches || toMatches);
    }) : [];
    
    // For "load more" requests, we don't want to merge with all local transactions
    // as those are already in the transactions state, we only want new API transactions
    // to be added to what's already in state
    if (options.nextToken) {
      return {
        transactions: apiTransactions,
        hasMore,
        nextToken
      };
    }
    
    // For initial/refresh requests, merge API transactions with local transactions
    const mergedTransactions = mergeTransactions(relevantLocalTxs, apiTransactions);
    
    return {
      transactions: mergedTransactions,
      hasMore,
      nextToken
    };
    
  } catch (error) {
    console.error("Error fetching and merging transactions:", error);
    
    // Return local transactions if API fails
    const lowerWalletAddress = walletAddress.toLowerCase();
    const relevantLocalTxs = Array.isArray(localTransactions) ? localTransactions.filter(tx => {
      const isRelevantChain = tx.network === chainKey;
      const fromMatches = tx.from && tx.from.toLowerCase() === lowerWalletAddress;
      const toMatches = tx.to && tx.to.toLowerCase() === lowerWalletAddress;
      return isRelevantChain && (fromMatches || toMatches);
    }) : [];
    
    return {
      transactions: relevantLocalTxs,
      hasMore: false,
      nextToken: null
    };
  }
};